// sessionManager.ts - Smart Session Management with Multi-tab Support
import { authSessionStorageService } from './sessionStorageService'
import { authService } from './authService'
import axiosInstance from '@/lib/axiosInstance'
import { ControllerEndpoint } from './controllerEndpoint'

// 🔧 Configuration for optimal data analysis workflow
const SESSION_CONFIG = {
  sessionTimeout: 30 * 60 * 1000,     // 30 minutes (backend constraint)
  smartRefreshAt: 25 * 60 * 1000,     // Consider refresh at 25 minutes
  activityThreshold: 5 * 60 * 1000,   // Must have activity in last 5 minutes to refresh
  firstWarning: 27 * 60 * 1000,       // Warn at 27 minutes (3 min left)
  finalWarning: 29 * 60 * 1000,       // Final warning at 29 minutes (1 min left)
  
  // Multi-tab sync keys
  storageKeys: {
    lastActivity: 'session_last_activity',
    sessionTimer: 'session_timer_start',
    warningState: 'session_warning_state',
    tabActivity: 'session_tab_activity'
  }
} as const

// 🎯 Session state interface
interface SessionState {
  isActive: boolean
  loginTime: number
  lastActivity: number
  warningShown: 'none' | 'first' | 'final'
  timerId?: number
  refreshPromise?: Promise<boolean>
}

// 📱 Multi-tab event interface
interface TabSyncEvent {
  type: 'activity' | 'warning' | 'refresh' | 'logout'
  timestamp: number
  tabId: string
  data?: any
}

/**
 * Smart Session Manager with Multi-tab Support
 * Optimized for data analysis workflows with minimal interruption
 */
export class SessionManager {
  private state: SessionState = {
    isActive: false,
    loginTime: 0,
    lastActivity: 0,
    warningShown: 'none'
  }
  
  private tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  private activityCallbacks: Set<(active: boolean) => void> = new Set()
  private warningCallbacks: Set<(type: 'first' | 'final' | 'clear', timeLeft: number) => void> = new Set()
  private logoutCallbacks: Set<() => void> = new Set()
  
  private readonly DEBUG = process.env.NODE_ENV === 'development'

  constructor() {
    this.setupStorageListener()
    this.log('SessionManager initialized', { tabId: this.tabId })
  }

  /**
   * Initialize session management after login
   */
  initialize(): void {
    const sessionData = authSessionStorageService.getAllSessionData()
    
    if (!sessionData.loginTime || !sessionData.sessionId) {
      this.log('No valid session found for initialization')
      return
    }

    this.state = {
      isActive: true,
      loginTime: sessionData.loginTime,
      lastActivity: Date.now(),
      warningShown: 'none'
    }

    // Sync with other tabs
    this.updateStorageState()
    this.startSessionTimer()
    
    this.log('Session manager initialized', {
      loginTime: new Date(this.state.loginTime),
      sessionAge: Date.now() - this.state.loginTime
    })
  }

  /**
   * Record user activity (called by ActivityTracker)
   */
  recordActivity(): void {
    if (!this.state.isActive) return

    const now = Date.now()
    this.state.lastActivity = now
    
    // Clear any existing warnings
    if (this.state.warningShown !== 'none') {
      this.state.warningShown = 'none'
      this.notifyWarningCallbacks('clear', 0)
    }

    // Update storage for cross-tab sync
    localStorage.setItem(SESSION_CONFIG.storageKeys.lastActivity, now.toString())
    
    // Broadcast activity to other tabs
    this.broadcastToTabs({
      type: 'activity',
      timestamp: now,
      tabId: this.tabId
    })

    this.log('Activity recorded', { now: new Date(now) })
  }

  /**
   * Check if session refresh is needed and perform it
   */
  private async checkAndRefreshSession(): Promise<void> {
    if (!this.state.isActive || this.state.refreshPromise) return

    const now = Date.now()
    const sessionAge = now - this.state.loginTime
    const timeSinceActivity = now - this.state.lastActivity

    // Only refresh if:
    // 1. We're past the refresh threshold (25 minutes)
    // 2. User was active recently (within 5 minutes)
    const shouldRefresh = sessionAge >= SESSION_CONFIG.smartRefreshAt && 
                         timeSinceActivity <= SESSION_CONFIG.activityThreshold

    if (!shouldRefresh) {
      this.log('Skipping refresh', { 
        sessionAge: Math.round(sessionAge / 60000), 
        timeSinceActivity: Math.round(timeSinceActivity / 60000),
        reason: sessionAge < SESSION_CONFIG.smartRefreshAt ? 'too_early' : 'inactive_user'
      })
      return
    }

    this.log('Attempting smart session refresh...')
    
    try {
      // Use existing GET /session endpoint to refresh session
      this.state.refreshPromise = this.performSessionRefresh()
      const success = await this.state.refreshPromise
      
      if (success) {
        // Reset session timer with fresh 30 minutes
        this.state.loginTime = now
        this.state.warningShown = 'none'
        this.updateStorageState()
        this.restartSessionTimer()
        
        this.broadcastToTabs({
          type: 'refresh',
          timestamp: now,
          tabId: this.tabId,
          data: { newLoginTime: now }
        })
        
        this.log('Session refreshed successfully')
      } else {
        this.log('Session refresh failed')
      }
    } catch (error) {
      this.log('Session refresh error', error)
    } finally {
      this.state.refreshPromise = undefined
    }
  }

  /**
   * Perform actual session refresh using GET /session
   */
  private async performSessionRefresh(): Promise<boolean> {
    try {
      const response = await axiosInstance.get(
        ControllerEndpoint.SessionControllerEndpoint.SESSION
      )
      
      // If we get a successful response, session is refreshed server-side
      if (response.status === 200 && response.data) {
        // Update any session data if needed
        if (response.data.key) {
          await authSessionStorageService.setSecretKey(response.data.key)
        }
        return true
      }
      
      return false
    } catch (error) {
      this.log('Session refresh API call failed', error)
      return false
    }
  }

  /**
   * Start or restart the session timer
   */
  private startSessionTimer(): void {
    this.clearSessionTimer()
    
    const checkInterval = 30 * 1000 // Check every 30 seconds
    
    this.state.timerId = window.setInterval(() => {
      this.checkSessionStatus()
    }, checkInterval)
    
    this.log('Session timer started')
  }

  /**
   * Restart session timer (after refresh)
   */
  private restartSessionTimer(): void {
    this.log('Restarting session timer with fresh 30 minutes')
    this.startSessionTimer()
  }

  /**
   * Check current session status and handle warnings/timeouts
   */
  private checkSessionStatus(): void {
    if (!this.state.isActive) return

    const now = Date.now()
    const sessionAge = now - this.state.loginTime
    const timeLeft = SESSION_CONFIG.sessionTimeout - sessionAge

    // Check if session has expired
    if (timeLeft <= 0) {
      this.handleSessionTimeout()
      return
    }

    // Check for refresh opportunity
    if (sessionAge >= SESSION_CONFIG.smartRefreshAt) {
      this.checkAndRefreshSession()
    }

    // Handle warnings
    if (timeLeft <= (SESSION_CONFIG.sessionTimeout - SESSION_CONFIG.finalWarning) && 
        this.state.warningShown !== 'final') {
      this.state.warningShown = 'final'
      this.notifyWarningCallbacks('final', timeLeft)
      this.updateStorageState()
    } else if (timeLeft <= (SESSION_CONFIG.sessionTimeout - SESSION_CONFIG.firstWarning) && 
               this.state.warningShown === 'none') {
      this.state.warningShown = 'first'
      this.notifyWarningCallbacks('first', timeLeft)
      this.updateStorageState()
    }
  }

  /**
   * Handle session timeout
   */
  private handleSessionTimeout(): void {
    this.log('Session has timed out')
    
    this.state.isActive = false
    this.clearSessionTimer()
    
    // Broadcast logout to all tabs
    this.broadcastToTabs({
      type: 'logout',
      timestamp: Date.now(),
      tabId: this.tabId
    })
    
    // Notify logout callbacks
    this.logoutCallbacks.forEach(callback => {
      try {
        callback()
      } catch (error) {
        this.log('Error in logout callback', error)
      }
    })
  }

  /**
   * Clear session timer
   */
  private clearSessionTimer(): void {
    if (this.state.timerId) {
      clearInterval(this.state.timerId)
      this.state.timerId = undefined
    }
  }

  /**
   * Update localStorage state for cross-tab sync
   */
  private updateStorageState(): void {
    localStorage.setItem(SESSION_CONFIG.storageKeys.sessionTimer, this.state.loginTime.toString())
    localStorage.setItem(SESSION_CONFIG.storageKeys.lastActivity, this.state.lastActivity.toString())
    localStorage.setItem(SESSION_CONFIG.storageKeys.warningState, JSON.stringify({
      shown: this.state.warningShown,
      timestamp: Date.now()
    }))
  }

  /**
   * Broadcast event to other tabs via localStorage
   */
  private broadcastToTabs(event: TabSyncEvent): void {
    const eventData = JSON.stringify(event)
    localStorage.setItem(SESSION_CONFIG.storageKeys.tabActivity, eventData)
    
    // Remove the event after a short delay to trigger storage event
    setTimeout(() => {
      localStorage.removeItem(SESSION_CONFIG.storageKeys.tabActivity)
    }, 100)
  }

  /**
   * Setup storage event listener for cross-tab communication
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (event) => {
      if (event.key === SESSION_CONFIG.storageKeys.tabActivity && event.newValue) {
        try {
          const tabEvent: TabSyncEvent = JSON.parse(event.newValue)
          
          // Ignore events from this tab
          if (tabEvent.tabId === this.tabId) return
          
          this.handleTabEvent(tabEvent)
        } catch (error) {
          this.log('Error parsing tab event', error)
        }
      }
    })
  }

  /**
   * Handle events from other tabs
   */
  private handleTabEvent(event: TabSyncEvent): void {
    switch (event.type) {
      case 'activity':
        this.state.lastActivity = Math.max(this.state.lastActivity, event.timestamp)
        if (this.state.warningShown !== 'none') {
          this.state.warningShown = 'none'
          this.notifyWarningCallbacks('clear', 0)
        }
        this.log('Synced activity from another tab')
        break
        
      case 'refresh':
        if (event.data?.newLoginTime) {
          this.state.loginTime = event.data.newLoginTime
          this.state.warningShown = 'none'
          this.restartSessionTimer()
          this.notifyWarningCallbacks('clear', 0)
          this.log('Synced session refresh from another tab')
        }
        break
        
      case 'logout':
        this.handleSessionTimeout()
        break
    }
  }

  /**
   * Get current session info
   */
  getSessionInfo(): {
    isActive: boolean
    timeLeft: number
    sessionAge: number
    lastActivity: number
  } {
    const now = Date.now()
    const sessionAge = now - this.state.loginTime
    const timeLeft = Math.max(0, SESSION_CONFIG.sessionTimeout - sessionAge)
    
    return {
      isActive: this.state.isActive,
      timeLeft,
      sessionAge,
      lastActivity: this.state.lastActivity
    }
  }

  /**
   * Register callbacks for session events
   */
  onActivityChange(callback: (active: boolean) => void): () => void {
    this.activityCallbacks.add(callback)
    return () => this.activityCallbacks.delete(callback)
  }

  onWarning(callback: (type: 'first' | 'final' | 'clear', timeLeft: number) => void): () => void {
    this.warningCallbacks.add(callback)
    return () => this.warningCallbacks.delete(callback)
  }

  onLogout(callback: () => void): () => void {
    this.logoutCallbacks.add(callback)
    return () => this.logoutCallbacks.delete(callback)
  }

  /**
   * Notify warning callbacks
   */
  private notifyWarningCallbacks(type: 'first' | 'final' | 'clear', timeLeft: number): void {
    this.warningCallbacks.forEach(callback => {
      try {
        callback(type, timeLeft)
      } catch (error) {
        this.log('Error in warning callback', error)
      }
    })
  }

  /**
   * Manual session extension (for user-triggered refresh)
   */
  async extendSession(): Promise<boolean> {
    this.log('Manual session extension requested')
    this.recordActivity() // Reset activity timer
    return this.performSessionRefresh()
  }

  /**
   * Cleanup on logout
   */
  cleanup(): void {
    this.clearSessionTimer()
    this.state.isActive = false
    this.activityCallbacks.clear()
    this.warningCallbacks.clear()
    this.logoutCallbacks.clear()
    this.log('Session manager cleaned up')
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.DEBUG) {
      console.log(`[SessionManager-${this.tabId.slice(-4)}] ${message}`, data || '')
    }
  }
}

// Export singleton instance
export const sessionManager = new SessionManager()